import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { AUTH_PARTNERS } from '@farm-portal/core/auth/models/auth-partners.const';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { customAnimations } from '../animations/custom-animations';
import { RouteGroup, RouteInfo } from './sidebar.metadata';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  animations: customAnimations,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SidebarComponent implements OnDestroy {
  @Output() logoutEmit = new EventEmitter<void>();

  @Input() public menuGroups: RouteGroup[];
  @Input() public userClaims: string[];
  @Input() public user: string;
  @Input() public currentOrganizationId: string;
  @Input() public isBannerShown = false;

  depth: number;
  activeTitles: string[] = [];
  expanded: boolean;

  @Input() logoUrl: string;

  private destroy$ = new Subject<void>();

  constructor(public readonly translate: TranslateService) {
    if (this.depth === undefined) {
      this.depth = 0;
      this.expanded = true;
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  logout(): void {
    this.logoutEmit.emit();
  }

  toggleSlideInOut() {
    this.expanded = !this.expanded;
  }

  handleToggle(titles) {
    this.activeTitles = titles;
  }

  hasSubmenuAndHasNotExternalLink(item: RouteInfo): boolean {
    return item.submenu.length > 0 && !item.isExternalLink && this.isMenuItemVisible(item);
  }

  hasNotSubmenuAndExternalLink(item: RouteInfo): boolean {
    return item.submenu.length === 0 && !item.isExternalLink && this.isMenuItemVisible(item);
  }

  hasMenuExternalLink(item: RouteInfo): boolean {
    return item.isExternalLink && this.isMenuItemVisible(item);
  }

  hasProviderSection() {
    return AUTH_PARTNERS.some(partner => this.hasAccess(partner.permissionComponent));
  }

  getAuthPartners() {
    return AUTH_PARTNERS.map(x => {
      return { loginType: x.loginType, permissionComponent: x.permissionComponent };
    });
  }

  private hasAllowedOrganization(organizationIds?: string[]): boolean {
    if (organizationIds === undefined || organizationIds.length === 0) {
      return true;
    }

    if (this.currentOrganizationId === undefined || this.currentOrganizationId === null) {
      return false;
    }

    return organizationIds.includes(this.currentOrganizationId);
  }

  private hasAccessToComponents(components?: string[]): boolean {
    if (components === undefined) {
      return true;
    }

    if (components.length === 0) {
      return true;
    }

    if (this.userClaims === undefined) {
      return true;
    }

    return components.some(component => {
      return this.userClaims.some(claim => {
        return claim.startsWith(component);
      });
    });
  }

  private isMenuItemVisible(item: RouteInfo): boolean {
    return this.hasAllowedOrganization(item.organizationIds) && this.hasAccessToComponents(item.components);
  }

  isMenuItemForComponent(item: RouteInfo): boolean {
    return this.isMenuItemVisible(item);
  }

  isMenuGroupForComponent(item: RouteGroup): boolean {
    return this.hasAllowedOrganization(item.organizationIds) && this.hasAccessToComponents(item.components);
  }

  hasAccess(componentKey: string): boolean {
    return this.userClaims.some(claim => {
      return claim.startsWith(componentKey);
    });
  }
}
